import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const CreditContext = createContext();

// Time-based subscription thresholds.
//   - LOW_DAYS_THRESHOLD: warn the user when their subscription has
//     this many days (or fewer) left.
//   - EXPIRED: triggered when seconds_remaining === 0.
const LOW_DAYS_THRESHOLD = 2;

export function CreditProvider({ children }) {
  const [wallet, setWallet] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, true/false = known
  const lastWarningBalance = useRef(null);
  const retryCount = useRef(0);
  const hasFetchedOnce = useRef(false);

  const fetchWallet = useCallback(async (isRetry = false) => {
    try {
      if (!isRetry) {
        setLoading(true);
      }
      const res = await axios.get(`${API}/wallet`, { withCredentials: true });
      const newWallet = res.data;
      // Subscription-based access: warnings now speak in DAYS, not
      // credits. The wallet payload includes a `subscription` snapshot
      // (see /api/wallet on the backend) with seconds_remaining and
      // days_remaining.
      const sub = newWallet && newWallet.subscription ? newWallet.subscription : null;
      const subActive = !!(sub && sub.is_active);
      const secondsRemaining = sub ? (sub.seconds_remaining || 0) : 0;
      const daysRemaining = sub ? (sub.days_remaining || 0) : 0;
      // Only nudge users who have previously paid; brand-new visitors
      // discover /subscription via the hero CTA and don't need a scary
      // "expired" pop-up.
      const hasEverSubscribed = !!(sub && (sub.total_seconds_purchased || 0) > 0);

      if (newWallet && typeof newWallet.balance === 'number') {
        // Two distinct warning states keyed off the subscription:
        //   1. EXPIRED (no time left) → "Subscription expired"
        //   2. ABOUT TO EXPIRE (<= LOW_DAYS_THRESHOLD days left)
        // We use the days value as the "warning key" so we only fire
        // the toast each time the user crosses a new lower day.
        if (hasEverSubscribed && !subActive) {
          if (lastWarningBalance.current !== 'expired') {
            toast.error('Your subscription has expired. Renew to keep playing.', {
              duration: 5000,
              action: {
                label: 'Subscribe',
                onClick: () => window.location.href = '/subscription'
              }
            });
            lastWarningBalance.current = 'expired';
          }
        } else if (subActive && secondsRemaining > 0 && daysRemaining <= LOW_DAYS_THRESHOLD) {
          const warningKey = `low_${daysRemaining}`;
          if (lastWarningBalance.current !== warningKey) {
            const dayLabel = daysRemaining === 0
              ? 'less than a day'
              : `${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`;
            toast.warning(`Your subscription expires in ${dayLabel}.`, {
              duration: 4000,
              action: {
                label: 'Extend',
                onClick: () => window.location.href = '/subscription'
              }
            });
            lastWarningBalance.current = warningKey;
          }
        } else if (subActive) {
          // Healthy subscription → clear any pending warning so a
          // future drop re-fires cleanly.
          lastWarningBalance.current = null;
        }

        setWallet(newWallet);
        setError(null);
        setIsAuthenticated(true);
        retryCount.current = 0;
        hasFetchedOnce.current = true;
      } else {
        // Invalid response, retry once
        if (retryCount.current < 2) {
          retryCount.current++;
          setTimeout(() => fetchWallet(true), 500);
        }
      }
    } catch (err) {
      // User is not logged in
      if (err.response?.status === 401) {
        setIsAuthenticated(false);
        setWallet(null);
        hasFetchedOnce.current = true;
      } else {
        setError('Failed to fetch wallet');
        // Retry on network errors
        if (retryCount.current < 2 && !err.response) {
          retryCount.current++;
          setTimeout(() => fetchWallet(true), 1000);
          return;
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Check auth status on mount
  useEffect(() => {
    const checkAuthAndFetch = async () => {
      try {
        // First check if user is authenticated
        const authRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (authRes.data) {
          setIsAuthenticated(true);
          setUser(authRes.data);
          // User is authenticated, fetch wallet
          await fetchWallet();
        }
      } catch (err) {
        // User is not authenticated
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        hasFetchedOnce.current = true;
      }
    };

    checkAuthAndFetch();
  }, [fetchWallet]);

  // Refresh when window regains focus (user might have topped up in another tab)
  useEffect(() => {
    const handleFocus = () => {
      // Only refresh if user is known to be authenticated
      if (isAuthenticated === true) {
        fetchWallet(true);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchWallet, isAuthenticated]);

  const refreshBalance = useCallback(() => {
    return fetchWallet();
  }, [fetchWallet]);

  // Re-fetch the current user profile. Used after profile edits (e.g.
  // updating the certificate display name) so navbar avatar + future
  // certificates reflect the new value immediately.
  const refreshUser = useCallback(async () => {
    try {
      const authRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (authRes.data) setUser(authRes.data);
      return authRes.data;
    } catch (_) {
      return null;
    }
  }, []);

  // Called after successful login to fetch wallet
  const onLogin = useCallback(async () => {
    setIsAuthenticated(true);
    retryCount.current = 0;
    // Refresh user profile so the navbar avatar/name show up immediately
    // after login without waiting for a page navigation.
    try {
      const authRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (authRes.data) setUser(authRes.data);
    } catch (_) { /* ignore */ }
    return fetchWallet();
  }, [fetchWallet]);

  // Called after logout to clear state
  const onLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout API error:', error);
    }
    setIsAuthenticated(false);
    setWallet(null);
    setUser(null);
    setLoading(false);
    lastWarningBalance.current = null;
  }, []);

  // Check if user can afford an action
  // Subscription-gate replacement for the legacy "canAfford".
  //
  // The app pivoted from "wallet has >= X credits" to "user has an
  // active time-based subscription OR an unused free preview". Since
  // the legacy wallet `balance` is now always zeroed by the migration,
  // any check against `wallet.balance` would incorrectly fail for
  // every paying subscriber. We expose:
  //
  //   - canAfford(cost) → boolean: TRUE when the user can take a paid
  //     action (active sub OR free preview unused). Ignores `cost` and
  //     always treats `cost <= 0` as freely allowed.
  //   - hasActiveSubscription → boolean: TRUE only when expires_at > now.
  //   - hasFreePreview → boolean: TRUE when the one-shot preview is
  //     still available.
  //
  // Call sites still pass a `cost` for backwards compat — it's used
  // only as a sentinel to skip the gate when 0.
  const subSnap = wallet?.subscription || null;
  const hasActiveSubscription = !!(subSnap && subSnap.is_active);
  const hasFreePreview = !!(subSnap && !subSnap.free_preview_used);

  const canAfford = useCallback((cost) => {
    if (!cost || cost <= 0) return true;
    if (!wallet) return false;
    const s = wallet.subscription || null;
    if (s && s.is_active) return true;
    if (s && !s.free_preview_used) return true;
    return false;
  }, [wallet]);

  // Determine the display loading state
  // Show loading if: checking auth OR (authenticated and fetching wallet)
  const displayLoading = isAuthenticated === null || (isAuthenticated === true && loading && !hasFetchedOnce.current);

  const value = {
    wallet,
    balance: wallet?.balance ?? 0,
    loading: displayLoading,
    error,
    refreshBalance,
    refreshUser,
    isLoaded: !displayLoading && (isAuthenticated === false || wallet !== null),
    canAfford,
    hasActiveSubscription,
    hasFreePreview,
    subscription: subSnap,
    onLogin,
    onLogout,
    isAuthenticated,
    user,
    LOW_DAYS_THRESHOLD
  };

  return (
    <CreditContext.Provider value={value}>
      {children}
    </CreditContext.Provider>
  );
}

export const useCredits = () => {
  const context = useContext(CreditContext);
  if (!context) {
    throw new Error('useCredits must be used within a CreditProvider');
  }
  return context;
};

export default CreditContext;
